import { auth } from '../firebase';

/**
 * Document templates API (BBD-102). Thin wrappers over the backend's
 * /templates/ routes. Rendering always happens server-side — never
 * substitute {{variables}} in the browser.
 *
 * Refs are "preset:<slug>" (built-in) or "custom:<id>" (user-made).
 */

const API_BASE = 'https://bluebirddocumentationadmin.pythonanywhere.com/templates';

// On a fresh page load (a template deep link opened in a new tab, a Quick
// Link) Firebase restores the session asynchronously, so `auth.currentUser`
// is still null for a moment. Wait for that before deciding nobody is signed in.
const waitForAuth = async () => {
    if (auth.currentUser) return;
    if (typeof auth.authStateReady === 'function') {
        await auth.authStateReady();
        return;
    }
    await new Promise((resolve) => {
        const unsubscribe = auth.onAuthStateChanged(() => { unsubscribe(); resolve(); });
    });
};

const getAuthHeaders = async () => {
    await waitForAuth();
    const token = await auth.currentUser?.getIdToken();
    const uid = auth.currentUser?.uid;
    if (!token || !uid) {
        throw new Error('User not authenticated');
    }
    return {
        'auth-token': `Bearer ${token}`,
        Authorization: `${token}`,
        'requesting-user-uid': uid,
    };
};

class TemplateApiError extends Error {
    constructor(message, status, data) {
        super(message);
        this.name = 'TemplateApiError';
        this.status = status;
        this.data = data || {};
    }
}

const request = async (path, { method = 'GET', body, params } = {}) => {
    const headers = await getAuthHeaders();
    const query = params
        ? '?' + Object.entries(params)
            .filter(([, v]) => v !== undefined && v !== null && v !== '')
            .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
            .join('&')
        : '';
    const response = await fetch(`${API_BASE}${path}${query === '?' ? '' : query}`, {
        method,
        headers: { 'Content-Type': 'application/json', ...headers },
        body: body === undefined ? undefined : JSON.stringify(body),
    });
    let data = {};
    try {
        data = await response.json();
    } catch (e) {
        data = {};
    }
    if (!response.ok) {
        throw new TemplateApiError(data.error || data.message || `Request failed (${response.status})`, response.status, data);
    }
    return data;
};

/** The browser's IANA time zone, sent so dates render in the user's day. */
export const clientTimeZone = () => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch (e) {
        return 'UTC';
    }
};

export const clientLocale = () => {
    try {
        return navigator.language || 'en';
    } catch (e) {
        return 'en';
    }
};

export const isPresetRef = (ref) => typeof ref === 'string' && ref.startsWith('preset:');
export const isKit = (template) => template?.kind === 'kit';
export const isSnippet = (template) => template?.kind === 'snippet';

export const templatesApi = {
    TemplateApiError,

    /**
     * Everything the picker needs in one call:
     * { recent, workspace, personal, presets, categories, default_template_ref, default_template }
     */
    gallery({ workspaceId, kind, includeContent = true } = {}) {
        return request('/gallery/', {
            params: { workspace: workspaceId, kind, include_content: includeContent ? 1 : 0 },
        });
    },

    listPresets({ all = false, recording = false, kind, includeContent = true } = {}) {
        return request('/presets/', {
            params: { all: all ? 1 : undefined, recording: recording ? 1 : undefined, kind, include_content: includeContent ? 1 : 0 },
        });
    },

    getHiddenPresets() {
        return request('/presets/hidden/');
    },

    setHiddenPresets(slugs) {
        return request('/presets/hidden/', { method: 'PUT', body: { slugs } });
    },

    hidePreset(slug) {
        return request('/presets/hidden/', { method: 'PUT', body: { hide: slug } });
    },

    unhidePreset(slug) {
        return request('/presets/hidden/', { method: 'PUT', body: { unhide: slug } });
    },

    /** Personal templates, or a workspace's with { workspaceId }. */
    list({ workspaceId, kind, includeContent = true } = {}) {
        return request('/', { params: { workspace: workspaceId, kind, include_content: includeContent ? 1 : 0 } });
    },

    get(id) {
        return request(`/${id}/`);
    },

    /** payload: { name, description, category, icon, kind, title_pattern, description_pattern, content, fields, pages, workspace_id?, is_featured? } */
    create(payload) {
        return request('/create/', { method: 'POST', body: payload });
    },

    update(id, payload) {
        return request(`/${id}/update/`, { method: 'PATCH', body: payload });
    },

    remove(id) {
        return request(`/${id}/delete/`, { method: 'DELETE' });
    },

    duplicate(id, name) {
        return request(`/${id}/duplicate/`, { method: 'POST', body: name ? { name } : {} });
    },

    /** Copy to a workspace ({ workspaceId }) or to personal ({ workspaceId: null }). */
    copy(id, { workspaceId = null, name } = {}) {
        return request(`/${id}/copy/`, { method: 'POST', body: { workspace_id: workspaceId, name } });
    },

    /**
     * Resolve a template into { name, description, content, doc_type, kind, pages }.
     * preview=true fills missing fields with samples and doesn't count as a use.
     */
    render({ ref, values = {}, workspaceId, preview = false, transcriptionJobId } = {}) {
        return request('/render/', {
            method: 'POST',
            body: {
                ref,
                values,
                tz: clientTimeZone(),
                locale: clientLocale(),
                workspace_id: workspaceId || undefined,
                preview,
                transcription_job_id: transcriptionJobId || undefined,
            },
        });
    },

    /** Create a kit (space + child pages). Returns { space, pages, workspace_id }. */
    instantiate({ ref, values = {}, workspaceId } = {}) {
        return request('/instantiate/', {
            method: 'POST',
            body: { ref, values, tz: clientTimeZone(), locale: clientLocale(), workspace_id: workspaceId || undefined },
        });
    },
};

export default templatesApi;
